import { ProjectCard } from "@/components/projects/ProjectCard";
import { type PickedProject, ProjectPickerModal } from "@/components/projects/ProjectPickerModal";
import { SelectionProvider } from "@/contexts/SelectionContext";
import { useFragment } from "@/generated";
import { ProjectFieldsFragmentDoc, StudentProjectAccessDocument } from "@/generated/graphql";
import type { ProjectFields } from "@/types/Project";
import { Button, FormField } from "@ai-teach/ui-web";
import { useLazyQuery } from "@apollo/client/react";
import { useState } from "react";
import { LuCheck, LuChevronDown, LuFolderOpen, LuLanguages, LuX } from "react-icons/lu";
import {
  COPY,
  type Locale,
  translateCapLabel,
  translateDetail,
  translateLabel,
  translateRole,
} from "./-studentAccessCopy";

function CheckRow({
  label,
  passed,
  detail,
  locale,
}: {
  label: string;
  passed: boolean;
  detail: string;
  locale: Locale;
}) {
  return (
    <div className="flex gap-3 py-3 border-gray-200 border-b last:border-b-0 dark:border-gray-800">
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
          passed
            ? "bg-success-500/10 text-success-700 dark:text-success-500"
            : "bg-error-500/10 text-error-700 dark:text-error-400"
        }`}
      >
        {passed ? <LuCheck size={14} /> : <LuX size={14} />}
      </span>
      <div>
        <p className="label">{translateLabel(label, locale)}</p>
        <p className="body-sm text-muted">{translateDetail(detail, locale)}</p>
      </div>
    </div>
  );
}

export function StudentAccessPage() {
  const [locale, setLocale] = useState<Locale>("zh");
  const [student, setStudent] = useState("");
  const [project, setProject] = useState<PickedProject | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [askedAboutProject, setAskedAboutProject] = useState(false);

  const t = COPY[locale];

  const [check, { data, loading, error }] = useLazyQuery(StudentProjectAccessDocument, {
    fetchPolicy: "network-only",
  });

  const result = data?.studentProjectAccess;
  const visibleProjects = (result?.visibleProjects ?? []).map((project) =>
    useFragment(ProjectFieldsFragmentDoc, project)
  );
  const checks = result?.verdict?.checks ?? [];
  const failing = checks.filter((one) => !one.passed);
  const passing = checks.filter((one) => one.passed);

  const handleCheck = () => {
    if (!student.trim()) return;
    setAskedAboutProject(project !== null);
    check({
      variables: {
        student: student.trim(),
        projectId: project?.id ?? null,
      },
    });
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="h2 mb-1">{t.title}</h1>
          <p className="body-sm text-muted">{t.subtitle}</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0"
          leftIcon={<LuLanguages size={16} />}
          onClick={() => setLocale((current) => (current === "zh" ? "en" : "zh"))}
        >
          {t.otherLocale}
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="w-72">
          <FormField
            id="student"
            name="student"
            label={t.studentLabel}
            value={student}
            onChange={(e) => setStudent(e.target.value)}
            placeholder={t.studentPlaceholder}
          />
        </div>
        <div className="w-72">
          <p className="label mb-1">{t.projectLabel}</p>
          {project ? (
            <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-surface dark:border-gray-800 px-3 py-2">
              <LuFolderOpen size={16} className="shrink-0 text-muted" />
              <span className="body-sm truncate flex-1">{project.name}</span>
              <button
                type="button"
                onClick={() => setProject(null)}
                aria-label={t.clearProject}
                className="text-muted hover:text-secondary-200"
              >
                <LuX size={16} />
              </button>
            </div>
          ) : (
            <Button variant="secondary" onClick={() => setShowPicker(true)}>
              {t.chooseProject}
            </Button>
          )}
        </div>
        <Button onClick={handleCheck} disabled={loading || !student.trim()}>
          {loading ? t.checking : t.check}
        </Button>
      </div>

      <button
        type="button"
        onClick={() => setShowRules((open) => !open)}
        className="flex items-center gap-1 body-sm text-muted mb-8 hover:text-secondary-200"
      >
        <LuChevronDown
          size={16}
          className={`transition-transform ${showRules ? "" : "-rotate-90"}`}
        />
        {t.howItWorks}
      </button>
      {showRules && (
        <ul className="mb-8 -mt-6 flex list-disc flex-col gap-2 pl-5 body-sm text-muted">
          {t.rules.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      )}

      <ProjectPickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={setProject}
      />

      {error && <p className="body mb-6 text-error-600 dark:text-error-400">{error.message}</p>}

      {result && (
        <div className="flex flex-col gap-8">
          <section className="rounded-md border border-gray-200 bg-surface dark:border-gray-800 p-5">
            <div className="flex flex-wrap gap-x-10 gap-y-3">
              <div>
                <p className="caption text-muted">{t.studentLabel}</p>
                <p className="label">
                  {result.student.username} · {translateRole(result.student.role, locale)}
                </p>
              </div>
              <div>
                <p className="caption text-muted">{t.school}</p>
                <p className="label">{result.student.schoolName ?? t.noSchool}</p>
              </div>
              <div>
                <p className="caption text-muted">{t.classrooms}</p>
                <p className="label">
                  {result.student.classroomIds.length
                    ? result.student.classroomIds.length
                    : t.noClassrooms}
                </p>
              </div>
              <div>
                <p className="caption text-muted">{t.ticketExpires}</p>
                <p className="label">{result.student.ticketExpiresAt ?? t.noTicket}</p>
              </div>
            </div>

            {result.session ? (
              <div className="mt-5 border-gray-200 border-t pt-4 dark:border-gray-800">
                <p className="caption text-muted mb-2">
                  {t.signedIn(
                    new Date(result.session.createdAt).toLocaleString(
                      locale === "zh" ? "zh-TW" : "en-US"
                    )
                  )}
                </p>
                <div className="flex flex-wrap gap-2">
                  {result.session.caps.map((cap) => (
                    <span
                      key={cap.productId}
                      className={`caption rounded-full border px-2.5 py-1 ${
                        cap.held
                          ? "border-primary-500/30 bg-primary-500/10 text-primary-700 dark:text-primary-300"
                          : "border-gray-500/20 bg-gray-500/10 text-muted"
                      }`}
                      title={cap.held ? t.capHeld : t.capNotHeld}
                    >
                      {cap.productName}: {translateCapLabel(cap.capLabel, locale)}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-5 border-gray-200 border-t pt-4 dark:border-gray-800 body-sm text-error-600 dark:text-error-400">
                {t.noSession}
              </p>
            )}
          </section>

          {result.verdict && askedAboutProject && (
            <section>
              <h2 className="h3 mb-3">{result.verdict.visible ? t.visible : t.notVisible}</h2>
              <div className="rounded-md border border-gray-200 bg-surface dark:border-gray-800 px-5">
                {[...failing, ...passing].map((one) => (
                  <CheckRow
                    key={one.label}
                    label={one.label}
                    passed={one.passed}
                    detail={one.detail}
                    locale={locale}
                  />
                ))}
              </div>
            </section>
          )}

          {!askedAboutProject && (
            <section>
              <h2 className="h3 mb-4">{t.visibleProjects(visibleProjects.length)}</h2>
              {visibleProjects.length > 0 ? (
                <SelectionProvider>
                  <div className="grid grid-cols-2 gap-6 md:grid-cols-3 xl:grid-cols-4">
                    {visibleProjects.map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project as ProjectFields}
                        url="home"
                        isSelectionMode={false}
                      />
                    ))}
                  </div>
                </SelectionProvider>
              ) : (
                <p className="body-sm text-muted">{t.nothingVisible}</p>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
